/**
 * JobPublished — `/job-published` sourcing progress tracker.
 *
 * Shown immediately after a customer publishes a job. Polls the sourcing
 * job's progress (`useSourcingProgress`, `@/lib/api/apiClient`) and
 * renders a 4-stage tracker (Sourcing → Screening → Preparing Outreach →
 * Complete — `SOURCING_STAGES`, `@/lib/validation/sourcingProgress`),
 * animating through stages as polling responses come in, with
 * candidate counts updating live per stage. On `isComplete`, shows the
 * final counts and redirects the customer onward after a short delay.
 *
 * GitHub issue #498 ("E2E: Sourcing & outreach — pipeline tracking, email
 * management, replies"). See `@/lib/validation/sourcingProgress`'s module
 * doc-comment for the backend-endpoint TODO this page's polling depends
 * on.
 *
 * Route: reads `jobId` from the `?jobId=` query param (set by whatever
 * flow navigates here after publishing — the job-creation flow itself is
 * out of this issue's scope, see the "Job management" milestone issue).
 * Falls back to rendering a "no job to track" empty state if absent,
 * rather than crashing on an undefined `jobId`.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useSourcingProgress, reportApiError } from '@/lib/api/apiClient';
import { SOURCING_STAGES, stageIndex } from '@/lib/validation/sourcingProgress';

/** How long the "Complete" state is shown before auto-redirecting, so the
 * "Complete state shows final counts + redirect" test case can observe
 * the final counts before navigation happens. */
const REDIRECT_DELAY_MS = 3000;

export default function JobPublished() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('jobId') ?? undefined;
  const redirectTo = searchParams.get('redirectTo') ?? '/dashboard';

  const { data, isError, error } = useSourcingProgress(jobId);
  const [redirectScheduled, setRedirectScheduled] = useState(false);

  useEffect(() => {
    if (isError) reportApiError(error, 'Failed to load sourcing progress');
  }, [isError, error]);

  const currentStageIndex = data ? stageIndex(data.stage) : 0;

  useEffect(() => {
    if (!data?.isComplete) return;
    setRedirectScheduled(true);
    const timer = setTimeout(() => navigate(redirectTo, { replace: true }), REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [data?.isComplete, navigate, redirectTo]);

  const metricValue = useMemo(() => {
    if (!data) return {};
    return {
      candidatesSourced: data.candidatesSourced,
      candidatesScreened: data.candidatesScreened,
      candidatesQueued: data.candidatesQueued,
    };
  }, [data]);

  // Issue #564: "Contacted" is intentionally its own number, separate from
  // `candidatesQueued` ("Queued for outreach" above) — derived by the
  // backend ONLY from candidates with a confirmed outreach send
  // (`apps/workers/src/lib/sourcingProgressMetrics.ts`), so it can never
  // overstate the way the old customer-facing "Contacted" lens did
  // (counting merely-queued candidates as contacted).
  const candidatesContacted = data?.candidatesContacted ?? 0;

  if (!jobId) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background"
        data-testid="job-published-no-job"
      >
        <div className="text-center max-w-md">
          <p className="text-lg font-semibold">No job to track</p>
          <p className="mt-2 text-sm text-muted-foreground">
            This page shows sourcing progress right after you publish a job. Navigate here with a
            `?jobId=` to see it in action.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-4 py-2 border border-border rounded-md hover:bg-accent text-sm font-medium"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="sourcing-progress-page">
      <div className="container mx-auto max-w-2xl px-4 py-16">
        <h1 className="mb-2 text-center text-2xl font-bold">
          {data?.jobTitle ? `Sourcing candidates for ${data.jobTitle}` : 'Your job is live!'}
        </h1>
        <p className="mb-10 text-center text-sm text-muted-foreground">
          We're finding, screening, and reaching out to great candidates for you.
        </p>

        {/* 4-stage tracker */}
        <ol className="mb-10 flex items-start justify-between" data-testid="sourcing-stage-tracker">
          {SOURCING_STAGES.map((stage, index) => {
            const status =
              index < currentStageIndex || data?.isComplete
                ? 'done'
                : index === currentStageIndex
                  ? 'active'
                  : 'upcoming';
            const metric = stage.metricKey ? metricValue[stage.metricKey] : undefined;

            return (
              <li
                key={stage.key}
                className="flex flex-1 flex-col items-center text-center"
                data-testid={`sourcing-stage-${stage.key.toLowerCase()}`}
                data-stage-status={status}
              >
                <div
                  className={`mb-2 flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-semibold ${
                    status === 'done'
                      ? 'border-green-600 bg-green-600 text-white'
                      : status === 'active'
                        ? 'border-foreground text-foreground animate-pulse'
                        : 'border-border text-muted-foreground'
                  }`}
                >
                  {status === 'done' ? '✓' : index + 1}
                </div>
                <p className="text-xs font-semibold">{stage.label}</p>
                {stage.metricLabel && (
                  <p
                    className="mt-1 text-xs text-muted-foreground"
                    data-testid={`sourcing-stage-count-${stage.key.toLowerCase()}`}
                  >
                    {stage.metricLabel}:{' '}
                    <span className="font-semibold text-foreground">{metric ?? 0}</span>
                  </p>
                )}
              </li>
            );
          })}
        </ol>

        {data?.isComplete ? (
          <div
            className="rounded-lg border border-green-600/30 bg-green-50 p-6 text-center"
            data-testid="sourcing-complete-summary"
          >
            <p className="mb-3 text-lg font-semibold text-green-800">Sourcing complete!</p>
            <div className="mb-4 grid grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-2xl font-bold" data-testid="final-count-sourced">
                  {data.candidatesSourced}
                </p>
                <p className="text-muted-foreground">Sourced</p>
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="final-count-screened">
                  {data.candidatesScreened}
                </p>
                <p className="text-muted-foreground">Screened</p>
              </div>
              <div>
                <p className="text-2xl font-bold" data-testid="final-count-queued">
                  {data.candidatesQueued}
                </p>
                <p className="text-muted-foreground">Queued for outreach</p>
              </div>
              <div>
                {/* Issue #564: never labeled "Contacted" unless it's a
                    confirmed-send count — see `candidatesContacted`'s
                    doc-comment above and `sourcingProgress.ts`'s module
                    doc-comment for the full root-cause writeup. */}
                <p className="text-2xl font-bold" data-testid="final-count-contacted">
                  {candidatesContacted}
                </p>
                <p className="text-muted-foreground">Contacted</p>
              </div>
            </div>

            {/* GitHub issue #195/#522 ("[Email/UX] 'Sourcing complete —
                0 ready for outreach' must explain WHY"): a bare "Queued 0"
                reads as broken even when the mechanics worked. Whenever
                this run queued 0, show WHY — the reason breakdown the
                backend derived (`zeroQueuedReasonBreakdown`,
                `apps/workers/src/lib/zeroQueuedReasonBreakdown.ts`), or a
                visible placeholder if that data is unavailable. Never
                shown when `candidatesQueued` is >0 (this issue's [qa:4]). */}
            {data.candidatesQueued === 0 && (
              <div
                className="mb-4 rounded-md border border-border bg-background/60 p-3 text-left text-sm"
                data-testid="zero-queued-reason-breakdown"
              >
                <p className="font-medium text-foreground">
                  {data.zeroQueuedReasonBreakdown?.available
                    ? data.zeroQueuedReasonBreakdown.breakdownText
                    : 'Reason breakdown unavailable'}
                </p>
                {!!data.zeroQueuedReasonBreakdown?.available &&
                  data.zeroQueuedReasonBreakdown.needsEmailEnrichmentCount !== null &&
                  data.zeroQueuedReasonBreakdown.needsEmailEnrichmentCount > 0 && (
                    <p
                      className="mt-1 text-muted-foreground"
                      data-testid="needs-email-enrichment-count"
                      title="Candidates lacking a deliverable email address"
                    >
                      {data.zeroQueuedReasonBreakdown.needsEmailEnrichmentCount} need email
                      enrichment
                    </p>
                  )}
              </div>
            )}

            <p className="text-xs text-muted-foreground" data-testid="redirect-notice">
              Redirecting you in a moment…
            </p>
            <button
              onClick={() => navigate(redirectTo, { replace: true })}
              className="mt-3 px-4 py-2 bg-foreground text-background rounded-md text-sm font-medium"
              data-testid="sourcing-continue-button"
            >
              Continue now
            </button>
          </div>
        ) : (
          <p
            className="text-center text-sm text-muted-foreground"
            data-testid="sourcing-in-progress-notice"
          >
            This usually takes a couple of minutes — feel free to keep this tab open.
          </p>
        )}
        {redirectScheduled && <span className="sr-only" data-testid="redirect-scheduled" />}
      </div>
    </div>
  );
}
